/**
 * The seven multi-entity link types, in one table (ME3a).
 *
 * Forms, detail pages and route builders all used to spell "the seven
 * entity types" separately — three lists to keep in step. They read this
 * one instead.
 *
 * Deliberately NOT a generic registry: every field is explicit and typed
 * by convention, the search functions are real imports rather than
 * strings, and nothing here is looked up from user input. Adding a type
 * is an edit to this file, which is what makes it reviewable.
 *
 * Backend contract (ME2a/ME2b, both merged):
 *   write  ``<type>_ids: string[]``   — the full MANUAL set for that type
 *   read   ``<plural>``: [{link_id, id, label, is_manual, derived_from}]
 *
 * ``derived_from`` entries are ``{link_id, entity_type, entity_id}`` —
 * the DIRECT source only; derivation is flat by construction.
 */
import {
  fetchAdminApplication,
  fetchAdminApplicationsPage,
  fetchAdminLandlord,
  fetchAdminLandlordsPage,
  fetchAdminPropertyPickerOptions,
  fetchAdminRoomPickerOptions,
  fetchAdminTenanciesPage,
  fetchAdminTenancyById,
  fetchAdminTenant,
  fetchAdminTenantsPage,
  fetchAdminUnit,
  fetchAdminUnitsPage,
} from "../../../api/adminData";
import { applicantName, secondaryLine as applicationSecondary } from "../calendar/workReports/ApplicationPicker";
import { landlordContact, landlordName } from "../calendar/workReports/LandlordPicker";
import { propertyAddress, propertyLabel } from "../calendar/workReports/PropertyPicker";
import { roomContext, roomLabel } from "../calendar/workReports/RoomPicker";
import {
  primaryLine as tenancyPrimary,
  secondaryLine as tenancySecondary,
} from "../calendar/workReports/TenancyPicker";
import { tenantContact, tenantName } from "../calendar/workReports/TenantPicker";
import { unitLabel, unitLocation } from "../calendar/workReports/UnitPicker";
import { getRoomDetailPath } from "../rooms/roomConstants";
import { tenancyValue, unitValue } from "./entityLinkEntries";

/**
 * The label a PRE-ME2a response carries for one singular link.
 *
 * Five types keep it in a ``<type>_display_name`` sibling; unit and
 * tenancy compose theirs from two fields each, and those two compositions
 * already exist in ``entityLinkEntries`` — reused rather than repeated,
 * so the fallback reads exactly like the block it replaces.
 */
function displayNameLabel(row, type) {
  return (row?.[`${type}_display_name`] || "").trim() || null;
}

/**
 * One descriptor per type.
 *
 *   type          the backend's entity_type ("tenant")
 *   idsField      write field ("tenant_ids")
 *   arrayField    read field ("tenants")
 *   singularField legacy field, still read for the fallback ("tenant_id")
 *   legacyLabel   row => label, for a response without link arrays
 *   unavailableLabel  shown when a linked row cannot be named
 *   fetchById     optional id => row, ONLY used to name a selected id
 *                 that no read array covered (a create-mode prefill).
 *                 Property and room have none: their labels ride on
 *                 the owner row, exactly as in D4b.
 *   groupLabel    plural heading on a detail page ("Mieter")
 *   fieldLabel    form field label
 *   detailPath    id => route, or null when no page exists
 *   fetchPage     server search for the picker
 *   itemLabel     one-line name for a search row
 *   itemDetail    optional second line, or null
 */
export const ENTITY_LINK_TYPES = [
  {
    type: "tenant",
    idsField: "tenant_ids",
    arrayField: "tenants",
    singularField: "tenant_id",
    unavailableLabel: "Mieter nicht verfügbar",
    fetchById: fetchAdminTenant,
    legacyLabel: (row) => displayNameLabel(row, "tenant"),
    groupLabel: "Mieter",
    fieldLabel: "Mieter",
    searchPlaceholder: "Mieter nach Name, E-Mail oder Telefon suchen …",
    loadingText: "Mieter werden geladen …",
    emptyText: "Keine passenden Mieter gefunden.",
    errorText: "Mieter konnten nicht geladen werden.",
    detailPath: (id) => `/admin/tenants/${id}`,
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminTenantsPage({ q: query || undefined, limit, offset }),
    itemLabel: tenantName,
    itemDetail: tenantContact,
  },
  {
    type: "tenancy",
    idsField: "tenancy_ids",
    arrayField: "tenancies",
    singularField: "tenancy_id",
    unavailableLabel: "Mietverhältnis nicht verfügbar",
    fetchById: fetchAdminTenancyById,
    legacyLabel: tenancyValue,
    groupLabel: "Mietverhältnisse",
    fieldLabel: "Mietverhältnisse",
    searchPlaceholder: "Mietverhältnis suchen …",
    loadingText: "Mietverhältnisse werden geladen …",
    emptyText: "Keine passenden Mietverhältnisse gefunden.",
    errorText: "Mietverhältnisse konnten nicht geladen werden.",
    // No /admin/tenancies/:id route exists — tenancies live as cards on
    // the tenant page. Label only; a chevron is a promise.
    detailPath: () => null,
    // PICK1b: this client names its parameters `search` and `skip`
    // (api/adminData/tenancies.js — `tenancyListUrl`), NOT `q`/`offset`.
    // Passing the hook's names sent neither: every keystroke re-requested
    // the same unfiltered first page and "Mehr laden" could never
    // advance. `sort` opts into the picker ordering, which buckets by
    // status and tiebreaks on id — ordering only, no change to WHICH
    // rows come back (tenancy_admin_service.py:672-694), so cancelled
    // tenancies stay excluded exactly as before.
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminTenanciesPage({
        search: query || undefined,
        limit,
        skip: offset,
        sort: "picker_relevance",
      }),
    itemLabel: tenancyPrimary,
    itemDetail: tenancySecondary,
  },
  {
    type: "room",
    idsField: "room_ids",
    arrayField: "rooms",
    singularField: "room_id",
    unavailableLabel: "Zimmer nicht verfügbar",
    legacyLabel: (row) => displayNameLabel(row, "room"),
    groupLabel: "Zimmer",
    fieldLabel: "Zimmer",
    searchPlaceholder: "Zimmer suchen …",
    loadingText: "Zimmer werden geladen …",
    emptyText: "Keine passenden Zimmer gefunden.",
    errorText: "Zimmer konnten nicht geladen werden.",
    detailPath: (id) => getRoomDetailPath(id),
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminRoomPickerOptions({ q: query || undefined, limit, offset }),
    itemLabel: roomLabel,
    itemDetail: roomContext,
  },
  {
    type: "unit",
    idsField: "unit_ids",
    arrayField: "units",
    singularField: "unit_id",
    unavailableLabel: "Unit nicht verfügbar",
    fetchById: fetchAdminUnit,
    legacyLabel: unitValue,
    groupLabel: "Units",
    fieldLabel: "Units",
    searchPlaceholder: "Einheit suchen …",
    loadingText: "Einheiten werden geladen …",
    emptyText: "Keine passenden Einheiten gefunden.",
    errorText: "Einheiten konnten nicht geladen werden.",
    detailPath: (id) => `/admin/units/${id}`,
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminUnitsPage({ q: query || undefined, limit, offset }),
    itemLabel: unitLabel,
    itemDetail: unitLocation,
  },
  {
    type: "property",
    idsField: "property_ids",
    arrayField: "properties",
    singularField: "property_id",
    unavailableLabel: "Liegenschaft nicht verfügbar",
    legacyLabel: (row) => displayNameLabel(row, "property"),
    groupLabel: "Liegenschaften",
    fieldLabel: "Liegenschaften",
    searchPlaceholder: "Liegenschaft suchen …",
    loadingText: "Liegenschaften werden geladen …",
    emptyText: "Keine passenden Liegenschaften gefunden.",
    errorText: "Liegenschaften konnten nicht geladen werden.",
    detailPath: (id) => `/admin/properties/${id}`,
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminPropertyPickerOptions({ q: query || undefined, limit, offset }),
    itemLabel: propertyLabel,
    itemDetail: propertyAddress,
  },
  {
    type: "application",
    idsField: "application_ids",
    arrayField: "applications",
    singularField: "application_id",
    unavailableLabel: "Bewerbung nicht verfügbar",
    fetchById: fetchAdminApplication,
    legacyLabel: (row) => displayNameLabel(row, "application"),
    groupLabel: "Bewerbungen",
    fieldLabel: "Bewerbungen",
    searchPlaceholder: "Bewerbung suchen …",
    loadingText: "Bewerbungen werden geladen …",
    emptyText: "Keine passenden Bewerbungen gefunden.",
    errorText: "Bewerbungen konnten nicht geladen werden.",
    detailPath: (id) => `/admin/applications/${id}`,
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminApplicationsPage({ q: query || undefined, limit, offset }),
    itemLabel: applicantName,
    itemDetail: applicationSecondary,
  },
  {
    type: "landlord",
    idsField: "landlord_ids",
    arrayField: "landlords",
    singularField: "landlord_id",
    unavailableLabel: "Vermieter nicht verfügbar",
    fetchById: fetchAdminLandlord,
    legacyLabel: (row) => displayNameLabel(row, "landlord"),
    groupLabel: "Vermieter",
    fieldLabel: "Vermieter",
    searchPlaceholder: "Vermieter suchen …",
    loadingText: "Vermieter werden geladen …",
    emptyText: "Keine passenden Vermieter gefunden.",
    errorText: "Vermieter konnten nicht geladen werden.",
    detailPath: (id) => `/admin/landlords/${id}`,
    // PICK1b: this client destructures `search` (api/adminData/
    // landlords.js), so a `q` key was dropped before the URL was built
    // and typing filtered nothing. `offset`/`limit` were already the
    // real names, which is why paging worked while search did not. The
    // client's own `status: "all"` default is left untouched, so an
    // archived-but-linked landlord stays findable.
    fetchPage: ({ query, offset, limit }) =>
      fetchAdminLandlordsPage({ search: query || undefined, limit, offset }),
    itemLabel: landlordName,
    itemDetail: landlordContact,
  },
];

/** Same descriptors keyed by ``type``, for the derived-source lookups. */
export const ENTITY_LINK_TYPES_BY_NAME = Object.fromEntries(
  ENTITY_LINK_TYPES.map((d) => [d.type, d])
);

/** Every ``*_ids`` field name — the keys a payload may carry. */
export const ENTITY_IDS_FIELDS = ENTITY_LINK_TYPES.map((d) => d.idsField);

/**
 * The MANUAL ids of one type, as the form must start from.
 *
 * The read array is the truth when it exists: only ``is_manual`` links
 * are a person's choice, and initialising a derived one as manual would
 * silently promote it — the next save would then persist an automatic
 * link as a deliberate one, and it would survive its source disappearing.
 *
 * The fallback exists for a response written before ME2a (or any
 * endpoint that still answers with singular fields only). It must not
 * fire when the array is present-but-empty: that legitimately means "no
 * manual links", and falling back there would resurrect a stale id.
 */
export function manualIdsFor(row, descriptor) {
  const array = row?.[descriptor.arrayField];
  if (Array.isArray(array)) {
    return array
      .filter((link) => link && link.is_manual)
      .map((link) => String(link.id));
  }
  const legacy = row?.[descriptor.singularField];
  return legacy ? [String(legacy)] : [];
}

/**
 * ``{id, label}`` for every link of a type — what the multi-picker needs
 * to name an already-selected row without asking the server again.
 *
 * Includes derived links too: a link can be manual AND derived, and the
 * chip for it still needs a label.
 */
export function knownOptionsFor(row, descriptor) {
  const array = row?.[descriptor.arrayField];
  if (Array.isArray(array)) {
    return array
      .filter((link) => link && link.id)
      .map((link) => ({
        id: String(link.id),
        label: (link.label || "").trim() || null,
      }));
  }
  const legacy = row?.[descriptor.singularField];
  if (!legacy) return [];
  // Pre-ME2a shape: the label lives in a sibling ``*_display_name``.
  const label = (row?.[`${descriptor.type}_display_name`] || "").trim();
  return [{ id: String(legacy), label: label || null }];
}

/** The manual id set for EVERY type, keyed by ``*_ids`` field name. */
export function manualIdsByField(row) {
  const out = {};
  ENTITY_LINK_TYPES.forEach((d) => {
    out[d.idsField] = manualIdsFor(row, d);
  });
  return out;
}
